import { useEffect, useRef, useState } from "react";
import logger from "@/utils/logger";

/**
 * Custom hook that provides a real-time countdown for race start times
 *
 * @param {Date|string} targetDate - The date to count down to (Date object or ISO string)
 * @param {Object} options - Optional configuration
 * @param {boolean} options.includeSeconds - Whether to include seconds in the output (default: false)
 * @param {number} options.refreshInterval - How often to update the countdown in ms (default: 1000)
 * @returns {Object} Countdown state object
 */
export default function useCountdown(targetDate, options = {}) {
  const { includeSeconds = false, refreshInterval = 1000 } = options;

  // Parse the target date if it's a string
  const targetTime =
    targetDate && typeof targetDate === "string"
      ? new Date(targetDate)
      : targetDate;

  // State for the countdown values
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0,
    isComplete: !targetTime, // Mark as complete if no target time
    formattedTime: targetTime ? "" : "No time",
  });

  // Ref to store interval ID
  const intervalRef = useRef(null);

  // Ref to track if completion event has been fired
  const completionFiredRef = useRef(false);

  // Log when the hook is initialized
  useEffect(() => {
    if (!targetTime) {
      // logger.warn("[useCountdown] No target date provided");
      return;
    }

    // logger.log("[useCountdown] Initialized with target date:", targetTime);
    const now = new Date();
    const difference = targetTime - now;
    const secondsRemaining = Math.floor(difference / 1000);

    // logger.log("[useCountdown] Seconds remaining:", secondsRemaining);

    if (secondsRemaining <= 0) {
      // logger.log("[useCountdown] Target date is in the past");
    } else if (secondsRemaining <= 300) {
      // logger.log("[useCountdown] Less than 5 minutes remaining!");
    }
  }, [targetTime]);

  // Function to calculate time left
  const calculateTimeLeft = () => {
    if (!targetTime) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isComplete: true,
        formattedTime: "No time",
      };
    }

    const now = new Date();
    const difference = targetTime - now;

    // If the target date is in the past, clear the interval
    if (difference <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Only dispatch the event once when countdown completes
      if (!completionFiredRef.current && targetTime) {
        completionFiredRef.current = true;

        // Dispatch a custom event when countdown completes
        window.dispatchEvent(
          new CustomEvent("countdown-complete", {
            detail: {
              targetTime,
              targetTimeString:
                typeof targetDate === "string"
                  ? targetDate
                  : targetTime.toISOString(),
            },
          }),
        );
      }

      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isComplete: true,
        formattedTime: "Completed",
      };
    }

    // Calculate time components
    const totalSeconds = Math.floor(difference / 1000);
    const days = Math.floor(totalSeconds / (60 * 60 * 24));
    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    // Format the time string
    let formattedTime = "";

    if (days > 0) {
      formattedTime = `${days}d`;
    } else if (hours > 0) {
      formattedTime = `${hours}h`;
      if (minutes > 0) formattedTime += ` ${minutes}m`;
    } else if (minutes > 0) {
      formattedTime = `${minutes} min${minutes !== 1 ? "s" : ""}`;
    } else {
      formattedTime = `${seconds}s`;
    }

    // Include seconds if option is true and we're under 1 minute
    if (includeSeconds && totalSeconds < 60) {
      formattedTime = `${seconds}s`;
    }

    return {
      days,
      hours,
      minutes,
      seconds,
      totalSeconds,
      isComplete: false,
      formattedTime,
    };
  };

  // Set up the interval to update countdown
  useEffect(() => {
    // Reset completion flag when target changes
    completionFiredRef.current = false;

    // Initial calculation
    setCountdown(calculateTimeLeft());

    // Only set up interval if we have a valid target time
    if (targetTime) {
      // Set up interval
      intervalRef.current = setInterval(() => {
        const timeLeft = calculateTimeLeft();
        setCountdown(timeLeft);

        // Clear interval if countdown is complete
        if (timeLeft.isComplete && intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }, refreshInterval);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [targetTime?.getTime?.()]);

  return countdown;
}
